import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import VideoUpload from '../components/VideoUpload';
import ProcessingStatus from '../components/ProcessingStatus';

const UploadPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [analysisId, setAnalysisId] = useState(null);
  const [uploadData, setUploadData] = useState(null);

  const handleUploadSuccess = (data) => {
    setAnalysisId(data.analysisId);
    setUploadData(data);
  };

  const handleAnalysisComplete = (analysisData) => {
    navigate(`/analysis/${analysisId}`);
  };

  const handleAnalysisError = (error) => {
    console.error('Analysis error:', error);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!analysisId ? (
          <>
            {/* Page Header */}
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold text-gray-900 mb-3">
                {t('upload.title')}
              </h1>
              <p className="text-lg text-gray-600">
                {t('upload.subtitle')}
              </p>
            </div>

            {/* Upload Component */}
            <VideoUpload onUploadSuccess={handleUploadSuccess} />

            {/* How It Works Section */}
            <div className="mt-12 bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                How It Works
              </h2>
              <div className="grid md:grid-cols-4 gap-6">
                <Step
                  number={1}
                  title={t('upload.selectVideo')}
                  description="Upload your opponent match footage (MP4, AVI, MOV)"
                />
                <Step
                  number={2}
                  title="AI Analysis"
                  description="GPT-4 Vision detects formations and movements"
                />
                <Step
                  number={3}
                  title={t('analysis.tacticalInsights')}
                  description="Claude AI generates strategic recommendations"
                />
                <Step
                  number={4}
                  title={t('analysis.downloadReport')}
                  description="Download detailed tactical analysis report"
                />
              </div>
            </div>

            {/* Features List */}
            <div className="mt-8 grid md:grid-cols-3 gap-6">
              <FeatureCard
                title={t('analysis.formations')}
                description="Automatically identifies opponent formations with 85%+ accuracy"
                icon="⚽"
              />
              <FeatureCard
                title="Weakness Analysis"
                description="Pinpoints tactical vulnerabilities to exploit"
                icon="🎯"
              />
              <FeatureCard
                title="Training Plans"
                description="Generate 7-day drill schedules targeting weaknesses"
                icon="📋"
              />
            </div>
          </>
        ) : (
          <>
            {/* Processing Status */}
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold text-gray-900 mb-3">
                {t('analysis.processingVideo')}: {uploadData?.opponentTeam}
              </h1>
              <p className="text-lg text-gray-600">
                {t('analysis.pleaseWait')}
              </p>
            </div>

            <ProcessingStatus
              analysisId={analysisId}
              onComplete={handleAnalysisComplete}
              onError={handleAnalysisError}
            />
          </>
        )}
      </div>
    </div>
  );
};

// Step Component
const Step = ({ number, title, description }) => (
  <div className="text-center">
    <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-3">
      {number}
    </div>
    <h3 className="font-semibold text-gray-900 mb-2">{title}</h3>
    <p className="text-sm text-gray-600">{description}</p>
  </div>
);

// Feature Card Component
const FeatureCard = ({ title, description, icon }) => (
  <div className="bg-white rounded-lg shadow p-6 text-center hover:shadow-lg transition-shadow">
    <div className="text-4xl mb-4">{icon}</div>
    <h3 className="font-bold text-gray-900 mb-2">{title}</h3>
    <p className="text-sm text-gray-600">{description}</p>
  </div>
);

export default UploadPage;
